import React, { useState, useRef } from "react";
import CollectionsIcon from "@mui/icons-material/Collections";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import {
  useUploadPartnerGalleryImageMutation,
  useDeletePartnerGalleryImageMutation,
  useRenameGalleryCategoryMutation,
} from "src/store/services/partnerApi";
import { toast } from "react-toastify";
import { optionUnstyledClasses } from "@mui/base";
import { SocialGalleryItem } from "src/store/services/partnerApi";

interface social_categoryProps{
  
  image_url?: string;
  url?: string;
  category_name?: string;
  likes_count?: number;
  likers?: string[];
  file_size?: number;
  _id?: string;
}

interface GalleryViewProps {
  images: string[];
  category_name: string[];
  partnerId: string;
  onAddImage?: () => void;
  onDeleteImage?: (index: number) => void;
  social_gallery?: SocialGalleryItem[];
}

const GalleryView: React.FC<GalleryViewProps> = ({
  images,
  category_name,
  partnerId,
  onAddImage,
  onDeleteImage,
  social_gallery,
}) => {
  console.log(social_gallery);
  const [hoveredImage, setHoveredImage] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExisting, setExisting] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [category, setCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [oldCategory, setOldCategory] = useState("");
  const [previewUrls, setPreviewUrls] = useState<{ file: File; url: string }[]>(
    [],
  );
  const categories = [...new Set(social_gallery?.map(item => item.category_name))];
  const [imagesToDelete, setImagesToDelete] = useState<{
    urls: string[];
    indices: number[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadGalleryImage, { isLoading: isUploading }] =
    useUploadPartnerGalleryImageMutation();
  const [deleteGalleryImage, { isLoading: isDeleting }] =
    useDeletePartnerGalleryImageMutation();
  const [renameCategoryName, { isLoading: isRenaming }] =
    useRenameGalleryCategoryMutation();

  const handleAddImageClick = () => {
    setIsModalOpen(true);
  };
  const renameHandle = () => {
    setIsRenameModalOpen(true);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    // Validate all files
    files.forEach((file) => {
      if (!file.type.startsWith("image/")) {
        invalidFiles.push(`${file.name} - not a valid image file`);
        return;
      }
      if (file.size > maxSize) {
        invalidFiles.push(`${file.name} - size exceeds 5MB`);
        return;
      }
      validFiles.push(file);
    });

    // Show errors for invalid files
    if (invalidFiles.length > 0) {
      toast.error(
        `Some files were invalid:\n${invalidFiles.slice(0, 3).join("\n")}${
          invalidFiles.length > 3
            ? `\n...and ${invalidFiles.length - 3} more`
            : ""
        }`,
      );
    }

    if (validFiles.length === 0) return;

    setSelectedFiles(validFiles);

    // Create preview URLs for all valid files
    const previews: { file: File; url: string }[] = [];
    let loadedCount = 0;

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        previews.push({ file, url: reader.result as string });
        loadedCount++;
        if (loadedCount === validFiles.length) {
          setPreviewUrls(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleConfirmUpload = async () => {
    if (selectedFiles.length === 0) return;
    for (const category_new of category_name) {
      if (category_new === category && !isExisting) {
        toast.error("Category already exists.");
        return; // stop everything immediately
      }
    }

    try {
      await uploadGalleryImage({
        id: partnerId,
        media: selectedFiles,
        is_cover: "false",
        category_name: category, // if your API accepts it
      }).unwrap();

      toast.success(
        `${selectedFiles.length} image${
          selectedFiles.length > 1 ? "s" : ""
        } uploaded successfully`,
      );

      // Call the optional callback if provided
      if (onAddImage) {
        onAddImage();
      }

      // Close modal and reset state
      handleCloseModal();
    } catch (error: any) {
      toast.error(
        error?.data?.message || "Failed to upload images. Please try again.",
      );
    }
  };

  const handleCategoryRename = async () => {
    for (const category_new of category_name) {
      if (category_new === newCategory && !isExisting) {
        toast.error("Category already exists.");
        return; // stop everything immediately
      }
    }

    try {
      await renameCategoryName({
        partnerId: partnerId,
        formData: {
          old_category_name: oldCategory,
          new_category_name: newCategory,
        },
      }).unwrap();

      toast.success(
        `Category Name Updated Successfully`,
      );


      // Close modal and reset state
      handleCloseModal();
    } catch (error: any) {
      toast.error(
        error?.data?.message || "Failed to rename category. Please try again.",
      );
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsRenameModalOpen(false);
    setSelectedFiles([]);
    setPreviewUrls([]);
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemovePreview = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    const newPreviews = previewUrls.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
  };

  const handleImageSelect = (index: number) => {
    const newSelected = new Set(selectedImages);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedImages(newSelected);
  };

  const [showAll, setShowAll] = useState<Record<string, boolean>>({});

  const handleDeleteSelected = () => {
    if (selectedImages.size === 0) return;

    const urls = Array.from(selectedImages).map((index) => images[index]);
    const indices = Array.from(selectedImages);
    setImagesToDelete({ urls, indices });
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setImagesToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!imagesToDelete || imagesToDelete.urls.length === 0) return;

    try {
      await deleteGalleryImage({
        id: partnerId,
        image_urls: imagesToDelete.urls,
      }).unwrap();

      toast.success(
        `${imagesToDelete.urls.length} image${
          imagesToDelete.urls.length > 1 ? "s" : ""
        } deleted successfully`,
      );

      // Call the optional callback if provided
      if (onDeleteImage) {
        // Call for each deleted image (in reverse order to maintain indices)
        imagesToDelete.indices
          .sort((a, b) => b - a)
          .forEach((index) => {
            onDeleteImage(index);
          });
      }

      // Close modal and reset selection
      handleCloseDeleteModal();
      setSelectedImages(new Set());
    } catch (error: any) {
      toast.error(
        error?.data?.message || "Failed to delete images. Please try again.",
      );
    }
  };

  return (
    <>
      <div
        className="bg-white rounded-xl p-6 shadow-sm"
        style={{ border: "1px solid #0000001A" }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CollectionsIcon className="text-gray-700" fontSize="small" />
            <h2 className="text-lg font-semibold text-gray-900">
              Gallery View
            </h2>
          </div>
          <div className="flex items-center gap-2 ">
            {selectedImages.size > 0 && (
              <button
                onClick={handleDeleteSelected}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
              >
                <DeleteOutlineIcon fontSize="small" />
                <span>Delete Selected ({selectedImages.size})</span>
              </button>
            )}
            <button
              onClick={handleAddImageClick}
              className="flex items-center gap-2 px-4 py-2 bg-[#7c2ebd] text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <AddPhotoAlternateOutlinedIcon fontSize="small" />
              <span>Add Image</span>
            </button>
            <button
              onClick={renameHandle}
              className="flex items-center gap-2 px-4 py-2 bg-[#046108] text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              {/* <AddPhotoAlternateOutlinedIcon fontSize="small" /> */}
              <span>Rename Category</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Manage business photos and media
        </p>


        <div className="space-y-8">
  {categories.map((category) => {
    const categoryImages =
      social_gallery?.filter(
        (image) => image.category_name === category
      ) || [];

    const displayedImages = showAll[category || '']
      ? categoryImages
      : categoryImages.slice(0, 3);

    return (
      <div key={category}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">{category}</h2>

          {categoryImages.length > 3 && (
            <button
              onClick={() =>
                setShowAll((prev) => ({
                  ...prev,
                  [category  || '']: !prev[category  || ''],
                }))
              }
              className="text-sm font-medium text-[#7c2ebd] hover:underline"
            >
              {showAll[category  || ''] ? "Show Less" : "See All"}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {displayedImages.map((image) => {
            const index = social_gallery?.findIndex(
              (item) => item._id === image._id
            );

            return (
              <div
                key={image._id}
                className={`relative aspect-video rounded-lg overflow-hidden group ${
                  selectedImages.has(index || 0)
                    ? "ring-4 ring-[#7c2ebd]"
                    : ""
                }`}
                onMouseEnter={() => setHoveredImage(index || 0)}
                onMouseLeave={() => setHoveredImage(null)}
              >
                <img
                  src={image.image_url}
                  alt={image.category_name}
                  className="w-full h-full object-cover"
                />

                {/* Checkbox */}
                <div className="absolute top-2 left-2 z-10">
                  <input
                    type="checkbox"
                    checked={selectedImages.has(index || 0)}
                    onChange={() => handleImageSelect(index || 0)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 cursor-pointer accent-[#7c2ebd] rounded-full"
                  />
                </div>

                {/* Hover overlay */}
                {hoveredImage === index && (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center p-3">
                    <button
                      onClick={() => {
                        setImagesToDelete({
                          urls: [image.image_url || ""],
                          indices: [index],
                        });
                        setIsDeleteModalOpen(true);
                      }}
                      className="flex justify-center items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                    >
                      <DeleteOutlineIcon fontSize="small" />
                      <span>Delete</span>
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  })}
</div>
      </div>



      {/* Upload Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleCloseModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseIcon />
            </button>

            {/* Modal Header */}
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Upload Gallery Image
            </h3>

            <div className="mb-4 bg-white border-0 rounded-lg">
              <label className="flex items-center justify-between cursor-pointer">
                <span className=" text-black">Existing Category</span>

                <input
                  type="checkbox"
                  checked={isExisting}
                  onChange={() => setExisting(!isExisting)}
                  className="w-5 h-5 text-purple-700 border-2 border-purple-700 rounded focus:ring-2 focus:ring-purple-200"
                />
              </label>
            </div>
            {isExisting && (
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Category
                </label>

                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 text-black bg-white border-2 border-purple-700 rounded-lg outline-none transition-all duration-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-200"
                >
                  <option value="">Select Category</option>

                  {category_name.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {!isExisting && (
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Category (New)
                </label>
                <input
                  type="text"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full px-4 py-3 text-black bg-white border-2 border-purple-700 rounded-lg outline-none transition-all duration-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-200 placeholder:text-gray-400"
                  placeholder="Enter category..."
                />
              </div>
            )}
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Select Images
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-[#7c2ebd] file:text-white hover:file:bg-purple-700 cursor-pointer"
              />
              <p className="mt-1 text-xs text-gray-500">
                Maximum file size: 5MB per image. You can select multiple
                images.
              </p>
            </div>

            {/* Preview Grid */}
            {previewUrls.length > 0 && (
              <div className="mb-4">
                <label className="block mb-2 text-sm font-medium text-gray-700">
                  Preview ({previewUrls.length} image
                  {previewUrls.length > 1 ? "s" : ""})
                </label>
                <div className="grid grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                  {previewUrls.map((preview, index) => (
                    <div
                      key={index}
                      className="relative group rounded-lg overflow-hidden border border-gray-200"
                    >
                      <img
                        src={preview.url}
                        alt={`Preview ${index + 1}`}
                        className="w-full h-32 object-cover"
                      />
                      <button
                        onClick={() => handleRemovePreview(index)}
                        className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <CloseIcon fontSize="small" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs p-1 truncate">
                        {preview.file.name}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {isUploading && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">
                    Uploading {selectedFiles.length} image
                    {selectedFiles.length > 1 ? "s" : ""}...
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#7c2ebd] h-2 rounded-full animate-pulse"></div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseModal}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={selectedFiles.length === 0 || isUploading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#7c2ebd] rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Uploading...
                  </>
                ) : (
                  `Confirm Upload${
                    selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""
                  }`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {isRenameModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleCloseModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <CloseIcon />
            </button>

            {/* Modal Header */}
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Rename Category
            </h3>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Category
              </label>

              <select
                value={oldCategory}
                onChange={(e) => setOldCategory(e.target.value)}
                required
                className="w-full px-4 py-3 text-black bg-white border-2 border-purple-700 rounded-lg outline-none transition-all duration-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-200"
              >
                <option value="">Select Category</option>

                {category_name.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Category (New)
              </label>
              <input
                type="text"
                required
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                className="w-full px-4 py-3 text-black bg-white border-2 border-purple-700 rounded-lg outline-none transition-all duration-200 focus:border-purple-500 focus:ring-4 focus:ring-purple-200 placeholder:text-gray-400"
                placeholder="Rename category..."
              />
            </div>

            {/* Upload Progress */}
            {isRenaming && (
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-gray-600">
                    Updating category name...
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-[#7c2ebd] h-2 rounded-full animate-pulse"></div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseModal}
                disabled={isRenaming}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleCategoryRename}
                disabled={newCategory === "" || isRenaming}
                className="px-4 py-2 text-sm font-medium text-white bg-[#7c2ebd] rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRenaming ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Renaming...
                  </>
                ) : (
                  `Confirm update`
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && imagesToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={handleCloseDeleteModal}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
            {/* Close Button */}
            <button
              onClick={handleCloseDeleteModal}
              disabled={isDeleting}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <CloseIcon />
            </button>

            {/* Modal Header */}
            <div className="mb-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100">
                  <DeleteOutlineIcon className="text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  Delete {imagesToDelete.urls.length > 1 ? "Images" : "Image"}
                </h3>
              </div>
              <p className="text-sm text-gray-600 ml-16">
                Are you sure you want to delete{" "}
                {imagesToDelete.urls.length > 1
                  ? `these ${imagesToDelete.urls.length} images`
                  : "this image"}
                ? This action cannot be undone.
              </p>
            </div>

            {/* Images Preview */}
            <div className="mb-6">
              {imagesToDelete.urls.length === 1 ? (
                <div className="relative w-full h-48 rounded-lg overflow-hidden border border-gray-200">
                  <img
                    src={imagesToDelete.urls[0]}
                    alt="Image to delete"
                    className="w-full h-full object-contain"
                  />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {imagesToDelete.urls.map((url, idx) => (
                    <div
                      key={idx}
                      className="relative w-full h-32 rounded-lg overflow-hidden border border-gray-200"
                    >
                      <img
                        src={url}
                        alt={`Image ${idx + 1} to delete`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <span className="animate-spin inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <DeleteOutlineIcon fontSize="small" />
                    Delete {imagesToDelete.urls.length > 1 ? "Images" : "Image"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GalleryView;
